import { Component, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, NavigationSkipped, NavigationStart, Router } from '@angular/router';
import { BreadcrumbType } from '@core/types/breadcrums.types';
import { LoadCommunicationService } from '@pages/loads/load.communication.service';
import { ObjectNumbeBreadCrumb } from '@pages/quotes/quotes.types';
import { BreadcrumbService } from './service/breadcrumb.service';
import { PageHeaderService } from './service/page-header.service';
import { WizardProgressService } from './service/wizard-progress.service';

/** One crumb plus what the page header needs to know about it. */
interface HeaderCrumb extends BreadcrumbType {
  /** False for a section with no page of its own ("Quotes"), so the header never links to it. */
  linkable: boolean;
}

/**
 * The page header. On desktop it is one band level with the logo: the page's name, with a small link to the page
 * it sits under (Dan, 2026-10-16: the menu already shows the section, so a full breadcrumb row repeated it). Below
 * the desktop breakpoint, where the menu is hidden, the full breadcrumb trail is still shown.
 *
 * Route data it reads: `breadcrumb` (the label), `pageTitle` (a heading that differs from the label, e.g. the
 * Organizations tabs), `crumbLink: false` (a section with no page of its own), `parentTab` (on a record, the tab of
 * the page above that lists it, so "‹ Organizations" returns to Market Partners rather than the default tab).
 */
@Component({
  selector: 'app-breadcrumb',
  standalone: false,
  templateUrl: './app.breadcrumb.component.html',
  styleUrls: ['./app.breadcrumb.component.css']
})
export class AppBreadcrumbComponent {
  private breadcrumService = inject(BreadcrumbService);
  private loadCommunicationService = inject(LoadCommunicationService);
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private readonly pageHeader = inject(PageHeaderService);
  private readonly recordName = this.pageHeader.recordName;

  protected readonly crumbs = signal<HeaderCrumb[]>([]);
  private readonly pageTitleData = signal<string | null>(null);
  private readonly parentTab = signal<string | null>(null);
  /**
   * The record number a page publishes ("Contract #106"). Cleared when navigation starts: the number is now the
   * page title, so one left behind by a page that never reset it would rename the next page.
   */
  protected readonly objectNumber = signal<ObjectNumbeBreadCrumb | null>(null);
  /** The quote-v2 wizard stepper, when a quote is being built and not yet booked on a carrier. */
  public readonly wizardProgress = inject(WizardProgressService).progress;

  /** The trail with a section's repeat of itself removed ("Manage Users / Manage Users"). */
  private readonly trail = computed(() =>
    this.crumbs().filter((crumb, index, all) => index === 0 || crumb.label !== all[index - 1].label)
  );

  /**
   * The narrow-screen trail above the page: every section crumb except one that only repeats the page's own title
   * ("Carriers / Carriers" became "Carriers", 2026-10-16 walkthrough).
   */
  protected readonly ancestors = computed(() => {
    const trail = this.trail();
    const up = this.parent();
    return trail
      .slice(0, -1)
      .filter((crumb) => crumb.label !== this.title())
      // The narrow trail's parent goes back to the same tab as the desktop link (F-029).
      .map((crumb) => (up && up.url === `${crumb.url}/${this.parentTab()}` ? up : crumb));
  });

  /** The route's own label: the heading, unless a record name or an object number says more. */
  private readonly routeLabel = computed(() => this.pageTitleData() ?? this.trail()[this.trail().length - 1]?.label ?? '');

  protected readonly title = computed(() => {
    const record = this.recordName();
    if (record) return record;
    const number = this.objectNumber();
    if (number?.objectNumber) {
      // "Load #:" names its own noun; a bare "#: " takes the route's ("Company", "Carrier").
      const noun = number.description.replace(/#:?\s*$/, '').trim() || this.routeLabel();
      return `${noun} #${number.objectNumber}`;
    }
    return this.routeLabel();
  });

  /** The nearest page above this one, when it is a different page with a different name. */
  protected readonly parent = computed(() => {
    const trail = this.trail();
    const current = trail[trail.length - 1];
    for (let index = trail.length - 2; index >= 0; index--) {
      const crumb = trail[index];
      if (crumb.linkable && crumb.label !== this.title() && crumb.url.replace(/\/$/, '') !== current?.url.replace(/\/$/, '')) {
        // A record names its list's tab, so the link goes back to it instead of the default tab (F-029).
        const tab = this.parentTab();
        return tab ? { ...crumb, url: crumb.url + '/' + tab } : crumb;
      }
    }
    return null;
  });

  /** With a record name as the heading, the route label (and the record's number) still says which record it is. */
  protected readonly kind = computed(() => {
    if (!this.recordName()) return null;
    const number = this.objectNumber()?.objectNumber;
    return number ? `${this.routeLabel()} #${number}` : this.routeLabel();
  });

  constructor() {
    this.loadCommunicationService.getCurrentQuoteNumber().subscribe((number) => this.objectNumber.set(number));
    // A record name or number belongs to the page that set it; a navigation to another page starts without one.
    // A query-param change on the same page (a tab, a filter) keeps it: the page does not reload to set it again.
    // Both are kept aside until the navigation lands: one a guard cancels (unsaved changes, Stay) or that fails
    // leaves the same page on screen, which must get its name back.
    // Only the LATEST navigation's cancel restores: one superseded by a newer navigation is not staying put.
    let leftBehind: { objectNumber: ObjectNumbeBreadCrumb | null; recordName: string | null } | null = null;
    let latestStartId = 0;
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationStart) {
        latestStartId = event.id;
        if (event.url.split('?')[0] === this.router.url.split('?')[0]) return;
        leftBehind ??= { objectNumber: this.objectNumber(), recordName: this.recordName() };
        this.objectNumber.set(null);
        this.pageHeader.setRecordName(null);
      } else if (event instanceof NavigationCancel || event instanceof NavigationError || event instanceof NavigationSkipped) {
        if (leftBehind && event.id === latestStartId) {
          if (this.objectNumber() === null) this.objectNumber.set(leftBehind.objectNumber);
          if (this.recordName() === null) this.pageHeader.setRecordName(leftBehind.recordName);
          leftBehind = null;
        }
      } else if (event instanceof NavigationEnd) {
        leftBehind = null;
        this.crumbs.set(this.getBreadcrumbs());
      }
    });
  }

  getBreadcrumbs(): HeaderCrumb[] {
    const breadcrumbs: HeaderCrumb[] = [];
    let currentRoute: ActivatedRoute | null = this.activatedRoute.root;
    let fullPath = '';
    let pageTitle: string | null = null;
    let parentTab: string | null = null;

    while (currentRoute) {
      const routeConfig = currentRoute.routeConfig;
      if (routeConfig && routeConfig.data) {
        let routePath = routeConfig.path || '';
        Object.keys(currentRoute.snapshot.params).forEach((paramKey) => {
          const paramValue = currentRoute!.snapshot.params[paramKey];
          routePath = routePath.replace(`:${paramKey}`, paramValue);
        });
        fullPath += `/${routePath}`;

        if (routeConfig.data['breadcrumb']) {
          breadcrumbs.push({
            label: routeConfig.data['breadcrumb'],
            routeId: routeConfig.data['routeId'],
            url: fullPath,
            linkable: routeConfig.data['crumbLink'] !== false
          });
        }
        pageTitle = routeConfig.data['pageTitle'] ?? pageTitle;
        parentTab = routeConfig.data['parentTab'] ?? null;
      }
      currentRoute = currentRoute.firstChild;
    }

    this.pageTitleData.set(pageTitle);
    this.parentTab.set(parentTab);
    return breadcrumbs;
  }

  onBreadcrumbsClick(item: BreadcrumbType) {
    this.breadcrumService.selectBreadcrumbItem(item);
  }
}
